/**
 * Drives `/auth/email-verify`, the page an advisor lands on from a mailbox-verification link.
 *
 * WHAT THE PAGE DOES, IN ORDER
 *
 *   1. take the verification token from the URL fragment and scrub it from the address bar;
 *   2. wait for an explicit click — mail scanners pre-fetch links, and a token consumed on page
 *      load would be spent before the advisor ever saw it;
 *   3. send the token to `/public/email-verification-token`, which records the verification;
 *   4. say so, and stay put.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 * NO SIGN-IN, AS OF 15 SEP 2026
 *
 * This hook used to redeem a Clerk sign-in ticket through `useTicketSignIn` and then record the
 * verification from the resulting session. Proving a mailbox never needed a sign-in, and every
 * emailed link was a live credential because of it. The link now carries a verification-only
 * token, so there is no ticket to redeem, no session to create, and no "signed in to a different
 * account" chooser to show.
 *
 * > [!IMPORTANT]
 * > Nothing this hook sends is trusted. The server derives the advisor and the address from the
 * > token's own row; the body carries only the token and an audit user agent. Tampering with it
 * > cannot verify a mailbox the link was not minted for.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { advisorEmailVerificationApi } from '@client/api/waterlily';
import { captureVerifyTokenOnce } from '@client/util/advisorEmailVerifyToken';

export type AdvisorEmailVerificationState =
  /** No token in the link, or the server refused it: unknown, used or expired. */
  | 'invalidLink'
  /** Token held, waiting for the advisor's click. */
  | 'confirm'
  | 'redeeming'
  | 'verified'
  /** The request failed for a reason that is worth retrying. */
  | 'recordFailed';

interface UseAdvisorEmailVerificationResult {
  state: AdvisorEmailVerificationState;
  /** Present once verified, so the page can say when the advisor must do this again. */
  expiresAt: string | null;
  emailAddress: string | null;
  confirm: () => void;
  retry: () => void;
}

/**
 * The server answers a bad token with 400, and one answer for every kind of bad. That is the one
 * failure a retry cannot fix, so it goes to the invalid-link screen rather than "try again".
 *
 * Reads the status from either shape a request helper tends to throw — `error.response.status` or
 * `error.status` — and treats anything unrecognised as retryable, the kinder default.
 */
function isRefusedLink(error: unknown): boolean {
  const candidate = error as { status?: unknown; response?: { status?: unknown } } | null;
  return (candidate?.response?.status ?? candidate?.status) === 400;
}

export function useAdvisorEmailVerification(): UseAdvisorEmailVerificationResult {
  const [token] = useState(() => captureVerifyTokenOnce());
  const [refused, setRefused] = useState(false);

  // Single use. A double click, or StrictMode's double effect run, must not send the token twice —
  // the second request would be refused and turn a success into the invalid-link screen.
  const hasSubmitted = useRef(false);

  const redeem = useMutation({
    mutationFn: (verifyToken: string) =>
      advisorEmailVerificationApi.redeem({
        token: verifyToken,
        userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
      }),
    onMutate: () => setRefused(false),
    onError: error => setRefused(isRefusedLink(error)),
  });

  const { mutate } = redeem;

  const confirm = useCallback(() => {
    if (!token || hasSubmitted.current) return;
    hasSubmitted.current = true;
    mutate(token);
  }, [token, mutate]);

  // Only reachable from the retry screen, after a failure the server did not refuse — so the token
  // is still unspent (the server gives it back if recording failed) and sending it again is right.
  const retry = useCallback(() => {
    if (!token) return;
    mutate(token);
  }, [token, mutate]);

  const state: AdvisorEmailVerificationState = useMemo(() => {
    if (!token || refused) return 'invalidLink';
    if (redeem.isPending) return 'redeeming';
    if (redeem.isSuccess) return 'verified';
    if (redeem.isError) return 'recordFailed';
    return 'confirm';
  }, [token, refused, redeem.isPending, redeem.isSuccess, redeem.isError]);

  return {
    state,
    expiresAt: redeem.data?.expiresAt ?? null,
    emailAddress: redeem.data?.emailAddress ?? null,
    confirm,
    retry,
  };
}
